import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import Navbar from "../../../components/Navbar2";
import PricingToggle from "../ai/misc/PricingToggle";
import DirectAddOns from "./misc/DirectAddOns";
import SelectedFeaturesFloating, {
  SelectedItem,
} from "../misc/SelectedFeaturesFloating";
import LifecyclePhasesSection from "../misc/LifecyclePhasesSection";

import {
  CheckCircle2,
  Zap,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
} from "lucide-react";
import { featuresService } from "../../../services/api/settings";
import {
  Feature,
  OfferingPlan,
  OfferingAddon,
} from "../../../store/types/settings";
import { offeringsService } from "../../../services/api/settings";
import formatPriceString from "../../../utility/formatPriceString";
import formatPrice from "../../../utility/formatPrice";

const DirectPay: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [billingType, setBillingType] = useState<"Monthly" | "Quarterly" | "Yearly">(() => {
    const freq = searchParams.get("frequency");
    return freq === "YEARLY" ? "Yearly" : freq === "QUARTERLY" ? "Quarterly" : "Monthly";
  });
  const [selectedDirectAddOnIds, setSelectedDirectAddOnIds] = useState<
    string[]
  >([]);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState<string[]>([]);
  const [aiAddOns, setAiAddOns] = useState<Feature[]>([]);
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [expandedPlan, setExpandedPlan] = useState<number | null>(null);

  const id = searchParams.get("Id");
  const directPlanTier = searchParams.get("directPlan");
  const frequency = searchParams.get("frequency");
  const aiTier = searchParams.get("tier");
  const [offeringPlans, setOfferingPlans] = useState<OfferingPlan[]>([]);
  const [offeringAddons, setOfferingAddons] = useState<OfferingAddon[]>([]);
  const [loading, setLoading] = useState(true);

  const [lifecyclePhases, setLifecyclePhases] = useState<any[]>([]);
  const [selectedLifecyclePhaseIds, setSelectedLifecyclePhaseIds] = useState<number[]>([]);

  const plans = offeringPlans.map((plan) => ({
    name: plan.title,
    subtitle: plan.subtitle,
    tier: plan.tier || "",
    monthlyPrice: parseFloat(plan.monthlyPrice),
    quarterlyPrice: plan.quarterlyPrice
      ? parseFloat(String(plan.quarterlyPrice))
      : parseFloat(plan.monthlyPrice) * 3,
    yearlyPrice: parseFloat(plan.yearlyPrice),
    features: plan.points,
    integrationServices:
      (plan.costBreakdown?.deploymentCosts?.length ?? 0) > 0
        ? plan
            .costBreakdown!.deploymentCosts.filter((d: any) => d && d.name)
            .map((d: any) => ({
              name: d.name,
              cost: d.price.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }),
            }))
        : plan.setupFee || plan.integrationCost
          ? [
              {
                name: "Standard Onboarding & Training",
                cost: (
                  parseFloat(plan.setupFee || plan.integrationCost || "0") || 0
                ).toLocaleString(),
              },
            ]
          : [],
  }));

  useEffect(() => {
    if (frequency === "YEARLY") setBillingType("Yearly");
    else if (frequency === "QUARTERLY") setBillingType("Quarterly");
    else setBillingType("Monthly");
    const addOns = searchParams.get("directAddOns");
    if (addOns) {
      setSelectedDirectAddOnIds(addOns.split(",").filter((i) => i));
    }
    const featIds = searchParams.get("featureIds");
    if (featIds) {
      setSelectedFeatureIds(featIds.split(",").filter((i) => i));
    }
  }, [searchParams, frequency]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const offeringsResponse = await offeringsService.getAll();
        if (offeringsResponse.status === "success" && offeringsResponse.data) {
          setOfferingPlans(offeringsResponse.data.DIRECT.plans);
          setOfferingAddons([
            ...offeringsResponse.data.DIRECT.addons,
            ...offeringsResponse.data.RECRUITER.addons,
          ]);
          if (offeringsResponse.data.LIFECYCLE?.phases) {
            setLifecyclePhases(offeringsResponse.data.LIFECYCLE.phases);
          }
        }
      } catch (error) {
        console.error("Error fetching direct payment data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // Read lifecyclePhaseIds from URL parameters
  useEffect(() => {
    const lifecyclePhaseIdsParam = searchParams.get("lifecyclePhaseIds");
    if (lifecyclePhaseIdsParam) {
      const idsArray = lifecyclePhaseIdsParam
        .split(",")
        .map(Number)
        .filter((id) => !isNaN(id));
      setSelectedLifecyclePhaseIds(idsArray);
    }
  }, [searchParams]);

  useEffect(() => {
    const fetchAiAddOns = async () => {
      try {
        if (id) {
          const response = await featuresService.getAllAvailable(id);
          if (response.status === "success" && response.data) {
            setAiAddOns(response.data);
          }
        }
      } catch (error) {
        console.error("Error fetching AI add-ons:", error);
      }
    };
    fetchAiAddOns();
  }, [id]);

  const handleDirectAddOnToggle = (id: string) => {
    setSelectedDirectAddOnIds((prev) => {
      const newIds = prev.includes(id)
        ? prev.filter((i) => i !== id)
        : [...prev, id];

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("directAddOns", newIds.join(","));
        } else {
          p.delete("directAddOns");
        }
        return p;
      });
      return newIds;
    });
  };
  const handleLifecyclePhaseToggle = (phaseId: number) => {
    setSelectedLifecyclePhaseIds((prev) => {
      const isSelected = prev.includes(phaseId);
      let newIds = isSelected
        ? prev.filter((id) => id !== phaseId)
        : [...prev, phaseId];

      const mvpPhase = lifecyclePhases.find(
        (p) => p.name && p.name.trim().toLowerCase() === "mvp foundation",
      );
      if (mvpPhase) {
        const mvpId = mvpPhase.id;
        const hasOthersSelected = newIds.some((id) => id !== mvpId);

        if (hasOthersSelected) {
          if (!newIds.includes(mvpId)) {
            newIds.push(mvpId);
          }
        } else {
          if (phaseId === mvpId && isSelected) {
            newIds = [];
          }
        }
      }

      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("lifecyclePhaseIds", newIds.join(","));
        } else {
          p.delete("lifecyclePhaseIds");
        }
        return p;
      });

      return newIds;
    });
  };
  const handleDirectAddOnRemove = (id: string) => {
    setSelectedDirectAddOnIds((prev) => {
      const newIds = prev.filter((i) => i !== id);
      setSearchParams((p) => {
        if (newIds.length > 0) {
          p.set("directAddOns", newIds.join(","));
        } else {
          p.delete("directAddOns");
        }
        return p;
      });
      return newIds;
    });
  };

  const currentPlan = plans.find((p) => p.tier === directPlanTier);
  const planFeatures = currentPlan
    ? currentPlan.features.map((f) => ({
        id: f,
        displayName: f,
        price: 0,
      }))
    : [];

  const selectedAddOns = offeringAddons.filter((addon) =>
    selectedDirectAddOnIds.includes(addon.id),
  );

  const isYearly = billingType === "Yearly";

  const allSelectedItems: SelectedItem[] = [
    // ...planFeatures,
    ...selectedAddOns.map((a: OfferingAddon) => ({
      id: a.id,
      displayName: a.name,
      category: (a.serviceType === "RECRUITER" ? "AI" : "DIRECT") as
        | "AI"
        | "DIRECT",
      price:
        a.price !== undefined && a.price !== null
          ? Number(a.price)
          : (a as any).pricing
            ? billingType === "Yearly"
              ? (a as any).pricing.yearlyPrice ||
                parseFloat(
                  ((a as any).pricing.yearly || "0").replace(/[₦,\s]/g, ""),
                ) ||
                0
              : billingType === "Quarterly"
                ? (a as any).pricing.quarterlyPrice ||
                  ((a as any).pricing.monthlyPrice ? (a as any).pricing.monthlyPrice * 3 : 0) ||
                  parseFloat(
                    ((a as any).pricing.quarterly || "0").replace(/[₦,\s]/g, ""),
                  ) ||
                  (parseFloat(
                    ((a as any).pricing.monthly || "0").replace(/[₦,\s]/g, ""),
                  ) * 3) ||
                  0
              : (a as any).pricing.monthlyPrice ||
                parseFloat(
                  ((a as any).pricing.monthly || "0").replace(/[₦,\s]/g, ""),
                ) ||
                0
            : 0,
    })),
    ...aiAddOns
      .filter(
        (a) =>
          selectedFeatureIds.includes(a.id) &&
          !selectedAddOns.some((s) => s.id === a.id),
      )
      .map((a) => ({
        id: a.id,
        displayName: a.displayName || a.name,
        category: "AI" as const,
        price: a.price,
      })),
    ...lifecyclePhases
      .filter((p) => selectedLifecyclePhaseIds.includes(p.id) || p.isMandatory)
      .map((p) => ({
        id: p.id.toString(),
        displayName: p.name,
        category: "LIFECYCLE" as any,
        price:
          p.setupFee +
          p.monthlySubscription *
            (billingType === "Yearly" ? 12 : billingType === "Quarterly" ? 3 : 1),
      })),
  ];

  const calculateFloatingTotal = () => {
    const total = allSelectedItems.reduce(
      (sum, item) =>
        sum + (item.category === "AI" ? item.price || 0 : item.price || 0),
      0,
    );
    return total.toLocaleString();
  };

  const handleContinue = () => {
    navigate(`/order-form?${searchParams.toString()}`);
  };

  return (
    <div className="min-h-screen bg-gray-50 font-abel pb-20">
      <Navbar title="Login" link="/login" />
      <div className="max-w-7xl mx-auto px-4 pt-10">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4 mb-2">
            <div
              onClick={() => history.back()}
              className="text-blue-600 hover:text-blue-800 cursor-pointer"
            >
              <ArrowLeft className="w-6 h-6" />
            </div>
            <h1 className="text-3xl font-medium text-gray-900">
              Review Selection
            </h1>
          </div>
          <button
            onClick={() => setShowAllPlans(!showAllPlans)}
            className="px-6 py-2 bg-white border border-blue-600 text-blue-600 rounded-full hover:bg-blue-50 transition-colors font-medium flex items-center gap-2"
          >
            <Zap className="w-4 h-4" />
            {showAllPlans ? "Hide other plans" : "Compare all plans"}
          </button>
        </div>

        <PricingToggle
          billingType={billingType}
          setBillingType={(type) => {
            setBillingType(type);
          setSearchParams((p) => {
            p.set("frequency", type === "Yearly" ? "YEARLY" : type === "Quarterly" ? "QUARTERLY" : "MONTHLY");
            return p;
          });
          }}
        />

        {showAllPlans && (
          <div className="mt-10 mb-16 animate-in fade-in slide-in-from-top-4 duration-500">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {plans.map((plan, index) => {
                const isSelected = plan.tier === directPlanTier;
                const isExpanded = expandedPlan === index;
                return (
                  <div
                    key={index}
                    className={`rounded-[20px] shadow-lg overflow-hidden flex flex-col bg-white border-2 transition-all duration-300 ${
                      isSelected
                        ? "border-blue-600 ring-4 ring-blue-50 scale-[1.02]"
                        : "border-transparent hover:border-gray-200"
                    }`}
                  >
                    <div className="p-8 flex flex-col h-full">
                      <div className="flex justify-between items-start mb-1">
                        <h3 className="text-2xl font-medium text-gray-900">
                          {plan.name}
                        </h3>
                        {isSelected && (
                          <span className="bg-blue-600 text-white text-[10px] uppercase px-2 py-0.5 rounded-full font-bold">
                            Selected
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-blue-600 font-medium mb-4">
                        {plan.subtitle}
                      </p>

                      <div className="mb-6">
                        <div className="flex items-baseline">
                          <span className="text-3xl font-normal text-gray-900">
                            <span className="text-lg align-super">N</span>
                            {(billingType === "Yearly"
                              ? plan.yearlyPrice
                              : billingType === "Quarterly"
                                ? plan.quarterlyPrice
                                : plan.monthlyPrice
                            ).toLocaleString()}
                          </span>
                          <span className="text-gray-500 ml-1 text-sm">
                            /{billingType === "Yearly" ? "year" : billingType === "Quarterly" ? "quarter" : "month"}
                          </span>
                        </div>
                        <div className="mt-2 text-[12px] font-medium text-[#616161]">
                          Setup fee:{" "}
                          <span className="text-black">
                            {(
                              parseFloat(
                                (plan as any).setupFee ||
                                  (plan as any).integrationCost ||
                                  "0",
                              ) || 0
                            ).toLocaleString()}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-3 mb-6 flex-grow">
                        {plan.features
                          .slice(0, isExpanded ? undefined : 4)
                          .map((f, i) => (
                            <div key={i} className="flex items-start gap-2.5">
                              <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                              <span className="text-gray-700 text-[13px]">
                                {f}
                              </span>
                            </div>
                          ))}
                      </div>

                      {plan.features.length > 4 && (
                        <button
                          onClick={() =>
                            setExpandedPlan(isExpanded ? null : index)
                          }
                          className="text-blue-600 text-xs font-semibold flex items-center gap-1 mb-6 hover:underline"
                        >
                          {isExpanded ? (
                            <>
                              Show less <ChevronUp className="w-3 h-3" />
                            </>
                          ) : (
                            <>
                              Show all features{" "}
                              <ChevronDown className="w-3 h-3" />
                            </>
                          )}
                        </button>
                      )}

                      {(plan as any).integrationServices?.some(
                        (s: any) => s.name,
                      ) && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedPlan(isExpanded ? null : index);
                          }}
                          className={`w-full py-2.5 px-3 mb-6 border rounded-lg font-m hover:transition-all flex items-center justify-center gap-2 border-gray-100 text-gray-500 hover:bg-gray-50`}
                        >
                          {isExpanded ? (
                            <>
                              <ChevronUp className="w-4 h-4" />
                              <span className="text-sm">Hide Details</span>
                            </>
                          ) : (
                            <>
                              <ChevronDown className="w-4 h-4" />
                              <span className="text-sm">View Details</span>
                            </>
                          )}
                        </button>
                      )}

                      {isExpanded &&
                        (plan as any).integrationServices?.some(
                          (s: any) => s.name,
                        ) && (
                          <div
                            className={`mb-6 pb-6 border-t pt-6 border-gray-100`}
                          >
                            <div className="flex justify-between items-center mb-4">
                              <h4 className={`text-sm font-bold text-gray-900`}>
                                Integration Services
                              </h4>
                              <span
                                className={`text-[10px] font-bold text-gray-400 uppercase tracking-wider`}
                              >
                                Cost (NGN)
                              </span>
                            </div>
                            <ul className="space-y-3">
                              {(plan as any).integrationServices
                                .filter((s: any) => s.name)
                                .map((service: any, i: number) => (
                                  <li
                                    key={i}
                                    className="flex items-start justify-between text-xs"
                                  >
                                    <span className="text-gray-500 leading-relaxed pr-4">
                                      {service.name}
                                    </span>
                                    <span className="font-bold text-gray-900 whitespace-nowrap">
                                      {service.cost}
                                    </span>
                                  </li>
                                ))}
                            </ul>
                          </div>
                        )}

                      {!isSelected ? (
                        <button
                          onClick={() => {
                            setSearchParams((p) => {
                              p.set("directPlan", plan.tier);
                              return p;
                            });
                          }}
                          className="w-full py-3 px-4 rounded-xl border-2 border-blue-600 text-blue-600 font-bold hover:bg-blue-600 hover:text-white transition-all flex items-center justify-center gap-2"
                        >
                          Switch to this plan
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      ) : (
                        <div className="w-full py-3 px-4 rounded-xl bg-blue-50 text-blue-600 font-bold text-center border border-blue-200">
                          Current Selection
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-8 mt-10">
          <div className="lg:col-span-1">
            {currentPlan && (
              <div className="bg-white rounded-[20px] shadow-xl p-8 border border-blue-100 ring-2 ring-blue-600 relative">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Selected Plan
                </div>
                <h3 className="text-2xl font-medium text-gray-900 mb-2">
                  {currentPlan.name}
                </h3>
                <p className="text-blue-600 text-sm font-medium mb-6">
                  {currentPlan.subtitle}
                </p>
                <div className="mb-8">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-normal text-gray-900">
                      <span className="text-lg align-super">N</span>
                      {formatPrice(
                        billingType === "Yearly"
                          ? currentPlan.yearlyPrice
                          : billingType === "Quarterly"
                            ? currentPlan.quarterlyPrice
                            : currentPlan.monthlyPrice,
                      )}
                    </span>
                    <span className="text-gray-500 ml-1 text-sm">
                      /{billingType === "Yearly" ? "year" : billingType === "Quarterly" ? "quarter" : "month"}
                    </span>
                  </div>
                  <div className="mt-2 text-[12px] font-medium text-[#616161]">
                    Setup fee:{" "}
                    <span className="text-black">
                      {(
                        parseFloat(
                          (currentPlan as any).setupFee ||
                            (currentPlan as any).integrationCost ||
                            "0",
                        ) || 0
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="space-y-4">
                  {currentPlan.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{f}</span>
                    </div>
                  ))}
                </div>

                {currentPlan.integrationServices &&
                  currentPlan.integrationServices.length > 0 && (
                    <div className="mt-8 pt-6 border-t border-gray-100">
                      <button
                        onClick={() =>
                          setExpandedPlan(expandedPlan === -1 ? null : -1)
                        }
                        className="w-full flex items-center justify-between text-left mb-4 group"
                      >
                        <span className="text-sm font-bold text-gray-900 group-hover:text-blue-600 transition-colors">
                          Integration Services
                        </span>
                        {expandedPlan === -1 ? (
                          <ChevronUp className="w-4 h-4 text-blue-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400 group-hover:text-blue-600" />
                        )}
                      </button>

                      {expandedPlan === -1 && (
                        <div className="space-y-4">
                          <div className="flex justify-between text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                            <span>Integration Services</span>
                            <span>Cost (NGN)</span>
                          </div>
                          <ul className="space-y-3">
                            {currentPlan.integrationServices.map(
                              (service, i) => (
                                <li
                                  key={i}
                                  className="flex items-start justify-between text-xs"
                                >
                                  <span className="text-gray-500 leading-relaxed pr-4">
                                    {service.name}
                                  </span>
                                  <span className="font-bold text-gray-900 whitespace-nowrap">
                                    {service.cost}
                                  </span>
                                </li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}

                <button
                  onClick={handleContinue}
                  className="w-full mt-10 py-4 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition shadow-lg hover:shadow-blue-200"
                >
                  Continue to Order Form
                </button>
              </div>
            )}
          </div>

          <div className="lg:col-span-2">
            <DirectAddOns
              organizationId={id ?? undefined}
              selectedIds={[...selectedDirectAddOnIds, ...selectedFeatureIds]}
              onToggle={(id) => {
                const isAi = aiAddOns.some((a) => a.id === id);
                if (isAi) {
                  setSelectedFeatureIds((prev) => {
                    const newIds = prev.includes(id)
                      ? prev.filter((i) => i !== id)
                      : [...prev, id];

                    setSearchParams((p) => {
                      if (newIds.length > 0)
                        p.set("featureIds", newIds.join(","));
                      else p.delete("featureIds");
                      return p;
                    });

                    return newIds;
                  });
                } else {
                  handleDirectAddOnToggle(id);
                }
              }}
              showAiRecruiterAddOns={!!aiTier}
            />
            <LifecyclePhasesSection
              phases={lifecyclePhases}
              selectedPhaseIds={selectedLifecyclePhaseIds}
              onTogglePhase={handleLifecyclePhaseToggle}
              loading={loading}
            />
          </div>
        </div>
      </div>

      <SelectedFeaturesFloating
        selectedItems={allSelectedItems}
        onRemove={(itemId) => {
          const numId = Number(itemId);
          if (!isNaN(numId) && selectedLifecyclePhaseIds.includes(numId)) {
            handleLifecyclePhaseToggle(numId);
          } else {
            const isAi = aiAddOns.some((a) => a.id === itemId);
            if (isAi) {
              setSelectedFeatureIds((prev) => {
                const newIds = prev.filter((i) => i !== itemId);
                setSearchParams((p) => {
                  if (newIds.length > 0)
                    p.set("featureIds", newIds.join(","));
                  else p.delete("featureIds");
                  return p;
                });
                return newIds;
              });
            } else {
              handleDirectAddOnRemove(itemId);
            }
          }
        }}
        totalPrice={calculateFloatingTotal()}
        billingType={billingType}
        title="Direct Selection"
      />
    </div>
  );
};

export default DirectPay;
